/**
 * Client-side API response cache.
 *
 * Split into two focused classes:
 * - RequestCache: URL-level TTL cache for GET responses
 * - RecordIndex: Secondary index for individual records from list responses
 */

interface CacheEntry {
  data: unknown
  expiresAt: number
}

// ─── TTL Rules ────────────────────────────────────────────────────────────────

const DEFAULT_TTL_RULES: Array<{ pattern: RegExp; ttlMs: number }> = [
  { pattern: /\/items\?/, ttlMs: 30 * 60 * 1000 },
  { pattern: /\/suppliers\?/, ttlMs: 30 * 60 * 1000 },
  { pattern: /\/supplier-item\?/, ttlMs: 30 * 60 * 1000 },
  { pattern: /.*/, ttlMs: 30_000 },
]

// ─── RequestCache ─────────────────────────────────────────────────────────────

export class RequestCache {
  private readonly cache = new Map<string, CacheEntry>()
  private readonly maxSize: number
  private readonly ttlRules: Array<{ pattern: RegExp; ttlMs: number }>

  constructor(maxSize = 100, ttlRules = DEFAULT_TTL_RULES) {
    this.maxSize = maxSize
    this.ttlRules = ttlRules
  }

  get<T>(url: string): T | null {
    const entry = this.cache.get(url)
    if (!entry) return null
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(url)
      return null
    }
    return entry.data as T
  }

  set(url: string, data: unknown): void {
    const ttl = this.getTtl(url)
    const expiresAt = Date.now() + ttl

    if (this.cache.size >= this.maxSize && !this.cache.has(url)) {
      const oldestKey = this.cache.keys().next().value
      if (oldestKey) this.cache.delete(oldestKey)
    }

    this.cache.set(url, { data, expiresAt })
  }

  invalidate(pattern: RegExp): void {
    for (const key of this.cache.keys()) {
      if (pattern.test(key)) {
        this.cache.delete(key)
      }
    }
  }

  clear(): void {
    this.cache.clear()
  }

  private getTtl(url: string): number {
    for (const rule of this.ttlRules) {
      if (rule.pattern.test(url)) return rule.ttlMs
    }
    return 60_000
  }
}

// ─── RecordIndex ──────────────────────────────────────────────────────────────

export class RecordIndex {
  private readonly index = new Map<string, CacheEntry>()

  set(type: string, key: string, data: unknown, expiresAt: number): void {
    this.index.set(`${type}:${key}`, { data, expiresAt })
  }

  get<T>(type: string, key: string): T | null {
    const indexKey = `${type}:${key}`
    const entry = this.index.get(indexKey)
    if (!entry) return null
    if (Date.now() > entry.expiresAt) {
      this.index.delete(indexKey)
      return null
    }
    return entry.data as T
  }

  clearByType(type: string): void {
    const prefix = `${type}:`
    for (const key of this.index.keys()) {
      if (key.startsWith(prefix)) this.index.delete(key)
    }
  }

  clear(): void {
    this.index.clear()
  }
}

// ─── Module-level singletons ──────────────────────────────────────────────────

export const requestCache = new RequestCache()
export const recordIndex = new RecordIndex()

// ─── Thin wrappers (preserves existing export surface) ────────────────────────

export const getCached = <T>(url: string) => requestCache.get<T>(url)

export const setCache = (url: string, data: unknown) => {
  requestCache.set(url, data)
  indexRecords(url, data)
}

/** Escape regex metacharacters so a plain path can be used as a literal matcher. */
const escapeRegExp = (literal: string): string => literal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

/**
 * Invalidate cached GET responses and secondary index entries for a write path.
 *
 * @param writePath clean leading path segment of the mutated resource, e.g.
 *   "/po", "/po-amendments", "/inventory". We match cached URLs that contain it
 *   (escaped, so it's treated literally) and clear the matching record index by
 *   a start-anchored prefix check so e.g. "/po-cancellations" still clears "po".
 */
export const invalidateCache = (writePath: string) => {
  requestCache.invalidate(new RegExp(escapeRegExp(writePath)))
  const pathPrefixToIndex: Array<{ pathPrefix: string; indexPrefix: string }> = [
    { pathPrefix: "/inventory", indexPrefix: "inventory" },
    { pathPrefix: "/po", indexPrefix: "po" },
    { pathPrefix: "/items", indexPrefix: "item" },
    { pathPrefix: "/suppliers", indexPrefix: "supplier" },
    { pathPrefix: "/work-orders", indexPrefix: "workorder" },
  ]
  for (const mapping of pathPrefixToIndex) {
    if (writePath.startsWith(mapping.pathPrefix)) {
      recordIndex.clearByType(mapping.indexPrefix)
    }
  }
}

export const clearCache = () => {
  requestCache.clear()
  recordIndex.clear()
}

export const getIndexed = <T>(type: string, key: string) => recordIndex.get<T>(type, key)

// ─── Record indexing ──────────────────────────────────────────────────────────

function indexRecords(url: string, data: unknown): void {
  if (!data || typeof data !== "object") return
  const resp = data as Record<string, unknown>
  const items = resp.data as Array<Record<string, unknown>> | undefined
  if (!Array.isArray(items)) return

  const ttl = 30 * 60 * 1000
  const expiresAt = Date.now() + ttl

  if (url.includes("/items?")) {
    for (const item of items) {
      if (item.code) recordIndex.set("item", String(item.code), item, expiresAt)
    }
  } else if (url.includes("/suppliers?")) {
    for (const supplier of items) {
      if (supplier.code) recordIndex.set("supplier", String(supplier.code), supplier, expiresAt)
    }
  } else if (url.includes("/inventory")) {
    for (const inv of items) {
      if (inv.itemCode) recordIndex.set("inventory", String(inv.itemCode), inv, expiresAt)
    }
  } else if (url.includes("/po")) {
    for (const po of items) {
      if (po.poNumber) recordIndex.set("po", String(po.poNumber), po, expiresAt)
    }
  } else if (url.includes("/work-orders")) {
    for (const wo of items) {
      if (wo.workOrderId) recordIndex.set("workorder", String(wo.workOrderId), wo, expiresAt)
    }
  }
}
